import React, { useEffect, useMemo, useState } from 'react'
import { DndContext, DragOverlay, PointerSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { ExternalLink, Plus, Search, StickyNote, Trash2, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { api } from '../lib/api'

const LEGACY_TASK_STORAGE_KEY_PREFIX = 'sgez.personalTaskBoard.v1'
const LEGACY_NOTE_STORAGE_KEY_PREFIX = 'sgez.personalTaskNotes.v1'
const LEGACY_IMPORT_MARK_PREFIX = 'sgez.personalWorkDbImported.v1'

const COLUMNS = [
  { key: 'TODO', label: '할 일' },
  { key: 'IN_PROGRESS', label: '진행 중' },
  { key: 'WAITING', label: '대기' },
  { key: 'DONE', label: '완료' },
]

const STATUS_FILTERS = [
  { key: 'ALL', label: '전체' },
  ...COLUMNS,
]

const PRIORITIES = [
  { value: 'NORMAL', label: '보통' },
  { value: 'HIGH', label: '높음' },
  { value: 'LOW', label: '낮음' },
]

const LINK_TYPES = [
  { value: 'NONE', label: '연결 안 함' },
  { value: 'CUSTOMER', label: '고객' },
  { value: 'CLAIM', label: '청구 신청' },
  { value: 'REQUEST', label: '설계 의뢰' },
  { value: 'CALENDAR', label: '일정관리' },
  { value: 'EXTERNAL', label: '외부 링크' },
  { value: 'MANUAL', label: '직접 입력' },
]

const EMPTY_FORM = {
  title: '',
  memo: '',
  priority: 'NORMAL',
  dueDate: '',
  linkType: 'NONE',
  relatedId: '',
  linkLabel: '',
  linkUrl: '',
}

function unwrap(res) {
  const body = res?.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

function pageItems(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(data?.content)) return data.content
  return []
}

function normalizeUrl(value) {
  const text = String(value || '').trim()
  if (!text) return ''
  if (text.startsWith('/')) return text
  if (/^https?:\/\//i.test(text)) return text
  return `https://${text}`
}

function normalizeTaskLinkUrl(linkType, value) {
  const text = String(value || '').trim()
  if (!text || linkType === 'NONE') return ''
  if (text.startsWith('/') || /^https?:\/\//i.test(text)) return text
  if (linkType === 'MANUAL') return `/${text}`
  return normalizeUrl(text)
}

function todayText() {
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

function dueTone(dueDate, status) {
  if (!dueDate || status === 'DONE') return 'text-slate-500'
  const today = todayText()
  if (dueDate < today) return 'text-red-600'
  if (dueDate === today) return 'text-amber-700'
  return 'text-slate-500'
}

function priorityClass(priority) {
  if (priority === 'HIGH') return 'border-red-200 bg-red-50 text-red-700'
  if (priority === 'LOW') return 'border-slate-200 bg-slate-50 text-slate-600'
  return 'border-sky-200 bg-sky-50 text-sky-700'
}

function columnLabel(status) {
  return COLUMNS.find((column) => column.key === status)?.label || status || ''
}

function linkTypeLabel(linkType) {
  return LINK_TYPES.find((item) => item.value === linkType)?.label || ''
}

function formatNoteTime(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`
}

function normalizeTaskFromApi(row) {
  return {
    id: row?.id,
    title: row?.title || '',
    memo: row?.memo || '',
    status: row?.status || 'TODO',
    priority: row?.priority || 'NORMAL',
    dueDate: row?.dueDate || '',
    linkType: row?.linkType || 'NONE',
    relatedId: row?.relatedId || '',
    linkLabel: row?.linkLabel || '',
    linkUrl: row?.linkUrl || '',
    sortOrder: row?.sortOrder || 0,
    createdAt: row?.createdAt || '',
    updatedAt: row?.updatedAt || '',
    completedAt: row?.completedAt || null,
  }
}

function normalizeNoteFromApi(row) {
  return {
    id: row?.id,
    body: row?.body || '',
    createdAt: row?.createdAt || '',
    updatedAt: row?.updatedAt || '',
  }
}

function readLegacyPersonalItems(prefix, userKey) {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(`${prefix}.${String(userKey || 'anonymous')}`)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function legacyImportMarked(userKey) {
  if (typeof window === 'undefined') return true
  try {
    return window.localStorage.getItem(`${LEGACY_IMPORT_MARK_PREFIX}.${String(userKey || 'anonymous')}`) === '1'
  } catch {
    return true
  }
}

function markLegacyImported(userKey) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(`${LEGACY_IMPORT_MARK_PREFIX}.${String(userKey || 'anonymous')}`, '1')
  } catch {
    // ignore
  }
}

async function importLegacyPersonalWorkIfNeeded(userKey, currentTasks, currentNotes) {
  if (legacyImportMarked(userKey)) {
    return { tasks: currentTasks, notes: currentNotes }
  }

  const legacyTasks = currentTasks.length > 0
    ? []
    : readLegacyPersonalItems(LEGACY_TASK_STORAGE_KEY_PREFIX, userKey).filter((task) => task?.title)
  const legacyNotes = currentNotes.length > 0
    ? []
    : readLegacyPersonalItems(LEGACY_NOTE_STORAGE_KEY_PREFIX, userKey).filter((note) => note?.body)

  if (legacyTasks.length === 0 && legacyNotes.length === 0) {
    markLegacyImported(userKey)
    return { tasks: currentTasks, notes: currentNotes }
  }

  const importedTasks = []
  for (const task of legacyTasks) {
    try {
      const linkType = task.linkType || (task.linkUrl ? 'MANUAL' : 'NONE')
      const res = await api.post('/work/personal-tasks', {
        title: String(task.title || '').trim(),
        memo: String(task.memo || '').trim(),
        status: task.status || 'TODO',
        priority: task.priority || 'NORMAL',
        dueDate: task.dueDate || null,
        linkType,
        relatedId: task.relatedId || '',
        linkLabel: task.linkLabel || '',
        linkUrl: normalizeTaskLinkUrl(linkType, task.linkUrl),
      })
      importedTasks.push(normalizeTaskFromApi(unwrap(res)))
    } catch {
      // ignore single-item import failures
    }
  }

  const importedNotes = []
  for (const note of legacyNotes) {
    try {
      const body = String(note.body || '').trim()
      if (!body) continue
      const res = await api.post('/work/personal-notes', { body })
      importedNotes.push(normalizeNoteFromApi(unwrap(res)))
    } catch {
      // ignore single-item import failures
    }
  }

  markLegacyImported(userKey)
  return {
    tasks: importedTasks.length > 0 ? importedTasks : currentTasks,
    notes: importedNotes.length > 0 ? importedNotes : currentNotes,
  }
}

function customerOptionLabel(row) {
  const name = row?.customerName || '고객'
  return row?.customerPhone ? `${name} · ${row.customerPhone}` : name
}

function claimOptionLabel(row) {
  return [row?.customerName, row?.insurerOrgName].filter(Boolean).join(' / ') || `청구 신청 ${row?.id || ''}`.trim()
}

function requestOptionLabel(row) {
  return row?.title || `설계 의뢰 ${row?.id || ''}`.trim()
}

function RelatedTargetFields({ value, onChange }) {
  const linkType = value.linkType || (value.linkUrl ? 'MANUAL' : 'NONE')
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const debouncedQuery = useDebouncedValue(query, 250)
  const needsSearch = linkType === 'CUSTOMER' || linkType === 'CLAIM' || linkType === 'REQUEST'

  useEffect(() => {
    setQuery('')
    setOptions([])
  }, [linkType])

  useEffect(() => {
    if (!needsSearch) return undefined
    let alive = true
    async function loadOptions() {
      setLoading(true)
      try {
        let res
        if (linkType === 'CUSTOMER') {
          res = await api.get('/customer-management/customers', {
            params: { q: debouncedQuery || undefined, page: 0, size: 8 },
            __silent: true,
          })
        } else if (linkType === 'CLAIM') {
          res = await api.get('/claims/applications/list', {
            params: { customerName: debouncedQuery || undefined, page: 0, size: 8 },
            __silent: true,
          })
        } else {
          res = await api.get('/requests', {
            params: { q: debouncedQuery || undefined, page: 0, size: 8, sort: '-createdAt' },
            __silent: true,
          })
        }
        if (alive) setOptions(pageItems(unwrap(res)).slice(0, 8))
      } catch {
        if (alive) setOptions([])
      } finally {
        if (alive) setLoading(false)
      }
    }
    loadOptions()
    return () => { alive = false }
  }, [debouncedQuery, linkType, needsSearch])

  const setLinkType = (nextType) => {
    if (nextType === 'NONE') {
      onChange({ ...value, linkType: 'NONE', relatedId: '', linkLabel: '', linkUrl: '' })
      return
    }
    if (nextType === 'CALENDAR') {
      onChange({ ...value, linkType: 'CALENDAR', relatedId: '', linkLabel: '일정관리', linkUrl: '/work/calendar' })
      return
    }
    onChange({ ...value, linkType: nextType, relatedId: '', linkLabel: '', linkUrl: '' })
  }

  const selectRelated = (row) => {
    if (!row?.id) return
    if (linkType === 'CUSTOMER') {
      onChange({
        ...value,
        linkType,
        relatedId: String(row.id),
        linkLabel: `${row.customerName || '고객'} 고객`,
        linkUrl: `/support/customers/${row.id}`,
      })
      return
    }
    if (linkType === 'CLAIM') {
      const label = claimOptionLabel(row)
      onChange({
        ...value,
        linkType,
        relatedId: String(row.id),
        linkLabel: `${label} 청구`,
        linkUrl: `/support/claims/${row.id}`,
      })
      return
    }
    const label = requestOptionLabel(row)
    onChange({
      ...value,
      linkType,
      relatedId: String(row.id),
      linkLabel: `${label} 설계 의뢰`,
      linkUrl: `/requests/${row.id}`,
    })
  }

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
      <div className="grid gap-3 sm:grid-cols-[12rem_minmax(0,1fr)]">
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">관련 화면</span>
          <select
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
            value={linkType}
            onChange={(e) => setLinkType(e.target.value)}
          >
            {LINK_TYPES.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>

        {needsSearch ? (
          <div className="min-w-0">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">검색</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`${linkTypeLabel(linkType)} 검색`}
              />
            </label>
            <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white">
              {loading ? (
                <div className="px-3 py-2 text-xs text-slate-500">검색 중...</div>
              ) : options.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-400">선택할 항목이 없습니다.</div>
              ) : (
                options.map((row) => {
                  const label = linkType === 'CUSTOMER'
                    ? customerOptionLabel(row)
                    : linkType === 'CLAIM'
                      ? claimOptionLabel(row)
                      : requestOptionLabel(row)
                  const selected = String(value.relatedId || '') === String(row.id || '')
                  return (
                    <button
                      key={`${linkType}-${row.id}`}
                      type="button"
                      className={`block w-full px-3 py-2 text-left text-xs hover:bg-slate-50 ${selected ? 'bg-sky-50 font-bold text-sgez-primary' : 'text-slate-700'}`}
                      onClick={() => selectRelated(row)}
                    >
                      <span className="line-clamp-2">{label}</span>
                    </button>
                  )
                })
              )}
            </div>
          </div>
        ) : null}

        {linkType === 'EXTERNAL' || linkType === 'MANUAL' ? (
          <div className="grid min-w-0 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">표시 이름</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
                value={value.linkLabel}
                onChange={(e) => onChange({ ...value, linkLabel: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">{linkType === 'EXTERNAL' ? 'URL' : '경로'}</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
                value={value.linkUrl}
                onChange={(e) => onChange({ ...value, linkUrl: e.target.value })}
              />
            </label>
          </div>
        ) : null}

        {linkType === 'CALENDAR' ? (
          <div className="flex items-end">
            <div className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700">
              기한 날짜에 일정 등록
            </div>
          </div>
        ) : null}
      </div>
      {value.linkUrl ? (
        <div className="mt-2 truncate text-xs font-semibold text-slate-500">
          {value.linkLabel || value.linkUrl}
        </div>
      ) : null}
    </div>
  )
}

function TaskForm({ value, onChange, onSubmit, submitLabel, onCancel }) {
  return (
    <form className="space-y-3" onSubmit={onSubmit}>
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">제목</span>
          <input
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
            value={value.title}
            onChange={(e) => onChange({ ...value, title: e.target.value })}
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">우선순위</span>
            <select
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
              value={value.priority}
              onChange={(e) => onChange({ ...value, priority: e.target.value })}
            >
              {PRIORITIES.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">기한</span>
            <input
              type="date"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
              value={value.dueDate}
              onChange={(e) => onChange({ ...value, dueDate: e.target.value })}
            />
          </label>
        </div>
      </div>
      <label className="mt-3 block">
        <span className="text-xs font-semibold text-slate-600">메모</span>
        <textarea
          rows={3}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
          value={value.memo}
          onChange={(e) => onChange({ ...value, memo: e.target.value })}
        />
      </label>
      <RelatedTargetFields value={value} onChange={onChange} />
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          onClick={onCancel}
        >
          취소
        </button>
        <button
          type="submit"
          className="inline-flex items-center gap-1 rounded-lg bg-sgez-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sgez-primary/90"
        >
          <Plus className="h-4 w-4" />
          {submitLabel}
        </button>
      </div>
    </form>
  )
}

function TaskCreateModal({ open, value, onChange, onSubmit, onClose }) {
  useEffect(() => {
    if (!open) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/40 px-4 py-6">
      <div className="absolute inset-0" onClick={onClose} />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="personal-task-create-title"
        className="relative max-h-[calc(100vh-3rem)] w-full max-w-3xl overflow-y-auto rounded-lg border border-slate-200 bg-white p-5 shadow-2xl"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 id="personal-task-create-title" className="text-lg font-bold text-slate-900">업무 추가</h2>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            title="닫기"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <TaskForm value={value} onChange={onChange} onSubmit={onSubmit} submitLabel="추가" onCancel={onClose} />
      </section>
    </div>
  )
}

export default function PersonalTaskBoardPage() {
  const { user } = useAuth()
  const userKey = user?.id || user?.userId || user?.email || user?.loginId || 'anonymous'
  const hasUser = Boolean(user)
  const [tasks, setTasks] = useState([])
  const [notes, setNotes] = useState([])
  const [form, setForm] = useState(EMPTY_FORM)
  const [noteDraft, setNoteDraft] = useState('')
  const [noteSearch, setNoteSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [activeDragTask, setActiveDragTask] = useState(null)
  const [selectedMobileStatus, setSelectedMobileStatus] = useState('TODO')
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
  )

  useEffect(() => {
    let alive = true
    async function loadPersonalWork() {
      setLoading(true)
      setError('')
      try {
        const [taskRes, noteRes] = await Promise.all([
          api.get('/work/personal-tasks'),
          api.get('/work/personal-notes'),
        ])
        if (!alive) return
        const loadedTasks = pageItems(unwrap(taskRes)).map(normalizeTaskFromApi)
        const loadedNotes = pageItems(unwrap(noteRes)).map(normalizeNoteFromApi)
        const migrated = await importLegacyPersonalWorkIfNeeded(userKey, loadedTasks, loadedNotes)
        if (!alive) return
        setTasks(migrated.tasks)
        setNotes(migrated.notes)
      } catch {
        if (alive) setError('내 업무보드를 불러오지 못했습니다.')
      } finally {
        if (alive) setLoading(false)
      }
    }
    if (hasUser) {
      loadPersonalWork()
    } else {
      setTasks([])
      setNotes([])
      setLoading(false)
    }
    return () => { alive = false }
  }, [hasUser, userKey])

  const counts = useMemo(() => {
    return Object.fromEntries(COLUMNS.map((column) => [
      column.key,
      tasks.filter((task) => task.status === column.key).length,
    ]))
  }, [tasks])

  const filteredNotes = useMemo(() => {
    const query = noteSearch.trim().toLowerCase()
    if (!query) return notes
    return notes.filter((note) => String(note.body || '').toLowerCase().includes(query))
  }, [noteSearch, notes])

  const mobileTasks = useMemo(() => {
    if (selectedMobileStatus === 'ALL') return tasks
    return tasks.filter((task) => task.status === selectedMobileStatus)
  }, [selectedMobileStatus, tasks])

  const addTask = async (e) => {
    e.preventDefault()
    const title = form.title.trim()
    if (!title) return
    if ((form.linkType || 'NONE') === 'CALENDAR' && !form.dueDate) {
      setError('일정 등록은 기한을 먼저 선택해야 합니다.')
      return
    }
    setError('')
    try {
      const payload = {
        title,
        memo: form.memo.trim(),
        priority: form.priority,
        dueDate: form.dueDate || null,
        linkType: form.linkType || 'NONE',
        relatedId: form.relatedId || '',
        linkLabel: form.linkLabel.trim(),
        linkUrl: normalizeTaskLinkUrl(form.linkType || 'NONE', form.linkUrl),
        status: 'TODO',
      }
      const res = await api.post('/work/personal-tasks', payload)
      const saved = normalizeTaskFromApi(unwrap(res))
      setTasks((prev) => [saved, ...prev.filter((task) => String(task.id) !== String(saved.id))])
      setForm(EMPTY_FORM)
      setCreateOpen(false)
    } catch {
      setError('업무를 저장하지 못했습니다.')
    }
  }

  const addNote = async (e) => {
    e.preventDefault()
    const body = noteDraft.trim()
    if (!body) return
    setError('')
    try {
      const res = await api.post('/work/personal-notes', { body })
      const saved = normalizeNoteFromApi(unwrap(res))
      setNotes((prev) => [saved, ...prev.filter((note) => String(note.id) !== String(saved.id))])
      setNoteDraft('')
    } catch {
      setError('업무노트를 저장하지 못했습니다.')
    }
  }

  const moveTask = async (taskId, status) => {
    const previous = tasks
    const now = new Date().toISOString()
    const current = tasks.find((task) => String(task.id) === String(taskId))
    if (!current || current.status === status) return
    setTasks((prev) => {
      const current = prev.find((task) => String(task.id) === String(taskId))
      if (!current || current.status === status) return prev
      const updated = {
        ...current,
        status,
        updatedAt: now,
        completedAt: status === 'DONE' ? (current.completedAt || now) : null,
      }
      return [updated, ...prev.filter((task) => String(task.id) !== String(taskId))]
    })
    setError('')
    try {
      const res = await api.patch(`/work/personal-tasks/${taskId}`, { status })
      const saved = normalizeTaskFromApi(unwrap(res))
      setTasks((prev) => [saved, ...prev.filter((task) => String(task.id) !== String(taskId))])
    } catch {
      setTasks(previous)
      setError('업무 상태를 저장하지 못했습니다.')
    }
  }

  const deleteTask = async (taskId) => {
    const previous = tasks
    setTasks((prev) => prev.filter((task) => String(task.id) !== String(taskId)))
    setError('')
    try {
      await api.delete(`/work/personal-tasks/${taskId}`)
    } catch {
      setTasks(previous)
      setError('업무를 삭제하지 못했습니다.')
    }
  }

  const deleteNote = async (noteId) => {
    const previous = notes
    setNotes((prev) => prev.filter((note) => String(note.id) !== String(noteId)))
    setError('')
    try {
      await api.delete(`/work/personal-notes/${noteId}`)
    } catch {
      setNotes(previous)
      setError('업무노트를 삭제하지 못했습니다.')
    }
  }

  return (
    <div className="mx-auto max-w-[120rem] px-4 py-6">
      <div className="mb-4 flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">내 업무보드</h1>
          {loading ? <p className="mt-1 text-xs font-semibold text-slate-500">불러오는 중</p> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-lg bg-sgez-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sgez-primary/90"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="h-4 w-4" />
            업무 추가
          </button>
          <Link
            className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            to="/work/calendar"
          >
            일정관리
          </Link>
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {error}
        </div>
      ) : null}

      <TaskCreateModal
        open={createOpen}
        value={form}
        onChange={setForm}
        onSubmit={addTask}
        onClose={() => setCreateOpen(false)}
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0">
          <MobileTaskList
            tasks={mobileTasks}
            totalCount={tasks.length}
            counts={counts}
            selectedStatus={selectedMobileStatus}
            onSelectStatus={setSelectedMobileStatus}
            onMove={moveTask}
            onDelete={deleteTask}
          />

          <div className="hidden md:block">
            <DndContext
              sensors={sensors}
              onDragStart={({ active }) => setActiveDragTask(active?.data?.current?.task || null)}
              onDragCancel={() => setActiveDragTask(null)}
              onDragEnd={({ over }) => {
                const task = activeDragTask
                const nextStatus = over?.id
                setActiveDragTask(null)
                if (task?.id && nextStatus && nextStatus !== task.status) moveTask(task.id, nextStatus)
              }}
            >
              <div className="-mx-2 overflow-x-auto px-2 pb-3">
                <div className="flex gap-3">
                  {COLUMNS.map((column) => (
                    <PersonalTaskColumn
                      key={column.key}
                      column={column}
                      tasks={tasks.filter((task) => task.status === column.key)}
                      count={counts[column.key] || 0}
                      activeTaskId={activeDragTask?.id}
                      onMove={moveTask}
                      onDelete={deleteTask}
                    />
                  ))}
                </div>
              </div>
              <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
                {activeDragTask ? <PersonalTaskCard task={activeDragTask} onMove={moveTask} onDelete={deleteTask} overlay /> : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>

        <WorkNotesPanel
          notes={filteredNotes}
          totalCount={notes.length}
          search={noteSearch}
          draft={noteDraft}
          onSearchChange={setNoteSearch}
          onDraftChange={setNoteDraft}
          onAdd={addNote}
          onDelete={deleteNote}
        />
      </div>
    </div>
  )
}

function WorkNotesPanel({ notes, totalCount, search, draft, onSearchChange, onDraftChange, onAdd, onDelete }) {
  return (
    <section className="flex min-h-[30rem] flex-col rounded-2xl border border-slate-200 bg-slate-50 xl:sticky xl:top-4 xl:h-[calc(100vh-12rem)] xl:min-h-[38rem]">
      <div className="border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <StickyNote className="h-4 w-4 shrink-0 text-sgez-primary" />
            <h2 className="truncate text-sm font-black text-slate-900">업무노트</h2>
          </div>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold tabular-nums text-slate-600">{totalCount}</span>
        </div>
        <form className="mt-3" onSubmit={onAdd}>
          <textarea
            rows={4}
            className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm leading-5 outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            placeholder="메모 입력"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="submit"
              className="inline-flex items-center gap-1 rounded-lg bg-sgez-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sgez-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!draft.trim()}
            >
              <Plus className="h-4 w-4" />
              추가
            </button>
          </div>
        </form>
        <label className="relative mt-3 block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="노트 검색"
          />
        </label>
      </div>

      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
        {notes.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white/80 px-3 py-8 text-center text-xs text-slate-400">
            {search.trim() ? '검색 결과 없음' : '노트가 없습니다'}
          </div>
        ) : (
          notes.map((note) => (
            <article key={note.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 whitespace-pre-wrap break-words text-sm leading-5 text-slate-700">{note.body}</p>
                <button
                  type="button"
                  className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  title="삭제"
                  onClick={() => onDelete(note.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-3 text-[11px] font-semibold text-slate-400">
                {formatNoteTime(note.createdAt)}
              </div>
            </article>
          ))
        )}
      </div>
    </section>
  )
}

function MobileTaskList({ tasks, totalCount, counts, selectedStatus, onSelectStatus, onMove, onDelete }) {
  const selectedLabel = selectedStatus === 'ALL' ? '전체' : columnLabel(selectedStatus)
  return (
    <section className="md:hidden">
      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="text-sm font-black text-slate-900">업무 상태</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {STATUS_FILTERS.map((filter) => {
            const selected = selectedStatus === filter.key
            const count = filter.key === 'ALL' ? totalCount : counts[filter.key] || 0
            return (
              <button
                key={filter.key}
                type="button"
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-2 text-sm font-bold transition ${
                  selected
                    ? 'border-sgez-primary bg-white text-slate-900 shadow-sm'
                    : 'border-slate-200 bg-white text-slate-600'
                }`}
                onClick={() => onSelectStatus(filter.key)}
              >
                <span>{filter.label}</span>
                <span className={`rounded-full px-1.5 py-0.5 text-[11px] tabular-nums ${
                  selected ? 'bg-sgez-primary/10 text-sgez-primary' : 'bg-slate-100 text-slate-500'
                }`}>
                  {count}
                </span>
              </button>
            )
          })}
        </div>
        <label className="mt-3 block">
          <span className="mb-1 block text-xs font-semibold text-slate-600">조회 상태</span>
          <select
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-sgez-primary focus:ring-2 focus:ring-sgez-primary/10"
            value={selectedStatus}
            onChange={(event) => onSelectStatus(event.target.value)}
          >
            {STATUS_FILTERS.map((filter) => {
              const count = filter.key === 'ALL' ? totalCount : counts[filter.key] || 0
              return (
                <option key={filter.key} value={filter.key}>
                  {filter.label} {count}
                </option>
              )
            })}
          </select>
        </label>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <h2 className="text-base font-black text-slate-900">{selectedLabel}</h2>
        <span className="text-xs font-semibold tabular-nums text-slate-500">{tasks.length}건</span>
      </div>
      <div className="mt-2 flex flex-col gap-2">
        {tasks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-8 text-center text-xs text-slate-400">
            {selectedLabel} 비어 있음
          </div>
        ) : (
          tasks.map((task) => (
            <PersonalTaskCard
              key={task.id}
              task={task}
              onMove={onMove}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
    </section>
  )
}

function PersonalTaskColumn({ column, tasks, count, activeTaskId, onMove, onDelete }) {
  const { isOver, setNodeRef } = useDroppable({ id: column.key })
  return (
    <section
      ref={setNodeRef}
      className={`flex h-[calc(100vh-12rem)] min-h-[38rem] w-[18rem] shrink-0 flex-col rounded-2xl border bg-slate-50 transition ${
        isOver ? 'border-sgez-primary ring-2 ring-sgez-primary/20' : 'border-slate-200'
      }`}
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2.5">
        <h2 className="text-sm font-black text-slate-900">{column.label}</h2>
        <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold tabular-nums text-slate-600">{count}</span>
      </div>
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2.5">
        {tasks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white/70 px-3 py-8 text-center text-xs text-slate-400">
            비어 있음
          </div>
        ) : (
          tasks.map((task) => (
            <DraggablePersonalTaskCard
              key={task.id}
              task={task}
              isActive={String(activeTaskId || '') === String(task.id)}
              onMove={onMove}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
    </section>
  )
}

function DraggablePersonalTaskCard({ task, isActive, onMove, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `personal-task-${task.id}`,
    data: { task },
  })
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging || isActive ? 0.35 : 1,
    touchAction: 'none',
  }
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <PersonalTaskCard task={task} onMove={onMove} onDelete={onDelete} />
    </div>
  )
}

function PersonalTaskCard({ task, onMove, onDelete, overlay = false }) {
  const linkIsInternal = task.linkUrl && task.linkUrl.startsWith('/')
  const linkLabel = task.linkLabel || task.linkUrl
  return (
    <article
      className={`rounded-xl border border-slate-200 bg-white p-3 shadow-sm ${overlay ? 'w-[18rem] rotate-1 cursor-grabbing shadow-2xl' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="break-words text-sm font-black text-slate-900">{task.title}</h3>
          {task.memo ? <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-5 text-slate-600">{task.memo}</p> : null}
        </div>
        <button
          type="button"
          className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
          title="삭제"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation()
            onDelete(task.id)
          }}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${priorityClass(task.priority)}`}>
          {PRIORITIES.find((item) => item.value === task.priority)?.label || '보통'}
        </span>
        {task.dueDate ? (
          <span className={`text-[11px] font-semibold ${dueTone(task.dueDate, task.status)}`}>{task.dueDate}</span>
        ) : null}
      </div>
      {task.linkUrl ? (
        <div className="mt-3">
          {task.linkType && task.linkType !== 'NONE' ? (
            <span className="mb-1 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">
              {linkTypeLabel(task.linkType)}
            </span>
          ) : null}
          {linkIsInternal ? (
            <Link
              className="inline-flex max-w-full items-center gap-1 truncate text-xs font-semibold text-sgez-primary hover:underline"
              to={task.linkUrl}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{linkLabel}</span>
            </Link>
          ) : (
            <a
              className="inline-flex max-w-full items-center gap-1 truncate text-xs font-semibold text-sgez-primary hover:underline"
              href={task.linkUrl}
              target="_blank"
              rel="noreferrer"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{linkLabel}</span>
            </a>
          )}
        </div>
      ) : null}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span className="inline-flex justify-center rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-500">
          {columnLabel(task.status)}
        </span>
        <select
          className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 sm:max-w-[9rem]"
          value={task.status}
          onPointerDown={(event) => event.stopPropagation()}
          onChange={(event) => {
            if (event.target.value !== task.status) onMove(task.id, event.target.value)
          }}
        >
          {COLUMNS.map((column) => (
            <option key={column.key} value={column.key}>{column.label}</option>
          ))}
        </select>
      </div>
    </article>
  )
}
